/**
 * Foundations, at `/foundations` (components/foundations/, docs/foundations.md).
 *
 * Seven small tasks every engineer meets, then a choice of discipline. The
 * models are web/src/lib/foundations.ts; this file is only what they say.
 *
 * Every quantity arrives already formatted — `q` below is a number and its
 * unit, digits in the reader's locale, wrapped in an isolate by the component
 * — so a sentence places a quantity but never builds one. Units are symbols
 * (W, mm, kN, m/s, V, A) and are not translated, for the reason boot.ts gives
 * for `MB`.
 *
 * Each step has a `leavesOut` line, in every language, because the honesty of
 * a simplified model is part of the lesson and part of the promise the page
 * makes about what finishing it means (docs/foundations.md §3).
 */

/* ── English ──────────────────────────────────────────────────────────────── */

export const en = {
  docTitle: 'Foundations · EngineerOS',
  kicker: 'Seven ideas every engineer uses',
  title: 'Foundations',
  lede:
    'Seven small tasks, about fifteen minutes in all. Each one starts from a film scene or an everyday object and asks ' +
    'you to make something work, and the page checks it. No account, nothing to download.',
  honest:
    'The models here are simplified on purpose, and every step says what its model leaves out. Finishing Foundations ' +
    'means you have used the core ideas, not that you can design anything real.',
  progress: (done: number, total: number) => `${done} of ${total} done`,
  stepLabel: (n: number) => `Step ${n}`,
  watch: 'Watch the Short',
  watchNote: 'opens on YouTube',
  check: 'Check',
  testing: 'Testing…',
  done: 'Done',
  next: 'Next step',
  leavesOutLabel: 'What this model leaves out',
  locked: 'Finish the step above to open this one.',
  irisName: 'Iris',

  steps: {
    estimate: {
      idea: 'Estimation',
      title: 'Off by ten, or by a thousand?',
      story:
        'In The Matrix, machines farm human body heat for power. Before checking a claim like that, an engineer asks ' +
        'one thing first: roughly how big is the number?',
      task:
        'Estimate how many watts a resting person gives off as heat. A clue: you eat about 2,000 kilocalories a day and ' +
        'nearly all of it leaves as heat. A kilocalorie is about 4,200 joules, a day is 86,400 seconds, and a watt is ' +
        'one joule each second.',
      debrief:
        'About 100 W, a bright old light bulb. A billion-watt power station would need ten million people, before any ' +
        'engine lost most of their heat. Getting the size right in a minute tells you whether a claim deserves an hour.',
      leavesOut: 'Body heat changes with size, activity and the room. Within about three times is all this step asks.',
    },
    loads: {
      idea: 'Forces and loads',
      title: 'Will the shelf sag?',
      story:
        'A one-metre alcove, two brackets and 25 kg of books. A shelf that bends where you can see it looks broken ' +
        'even when it is not, so builders keep the sag under a two-hundredth of the span. Here that is 5 mm.',
      task: 'Choose the thinnest pine plank that sags no more than 5 mm. Thicker wood costs more.',
      debrief:
        'Stiffness grows with the cube of thickness: 18 mm is one and a half times thicker than 12 mm and about 3.4 ' +
        'times stiffer. That is why floor joists stand tall and thin rather than lying flat and wide.',
      leavesOut: 'Knots in the wood, brackets that bend, screws that pull out, and wood that creeps under a load left for years.',
    },
    power: {
      idea: 'Energy and power',
      title: 'Three gigawatts through a wire',
      story:
        "In Iron Man, Tony Stark's reactor puts out three gigawatts, more than the Hoover Dam, through a few thin wires. " +
        'Power is volts times amps, so the same power can be a small current at a high voltage or a huge one at a low voltage.',
      task:
        'Get 3 GW down this line. Each cable carries 3,100 A at most, and the towers hold up to 1,000,000 V. Choose the ' +
        'voltage and the number of cables.',
      debrief:
        'Raise the voltage and the current falls, and current is what heats a wire. That one fact is why the grid carries ' +
        'power at hundreds of thousands of volts and steps it down near your home. The real Pacific DC Intertie carries ' +
        '3.1 GW at ±500,000 V, on two cables.',
      leavesOut: 'Losses along the line, AC against DC, the transformers at each end, and the weather.',
    },
    margin: {
      idea: 'Safety margins',
      title: 'How strong is strong enough?',
      story:
        'A hoist lifts 500 kg. A rope that holds exactly 500 kg fails the first time the load swings, the rope wears, or ' +
        'somebody loads 520 kg. So the brief asks for a rope five times stronger than the load.',
      task: 'Choose the thinnest steel wire rope that breaks at no less than five times the load.',
      debrief:
        'The margin pays for everything nobody measured: wear, the jolt as a load starts to move, a bad splice. Engineers ' +
        'choose the factor from what a failure would cost, which is why lifting people demands more than lifting bricks.',
      leavesOut:
        'Real ropes are rated by their maker and lifting rules set the factor. The rule of thumb here, breaking force of ' +
        'about 0.6 × d² kN, is only for this step.',
    },
    weakest: {
      idea: 'Failure',
      title: 'Which part breaks first?',
      story:
        'A load hangs from a hook, on a shackle, on a rope, from an anchor in the wall. The whole chain holds only what ' +
        'its weakest part holds, however strong the rest is.',
      task:
        'Make the chain hold 30 kN. You can afford three upgrades, and each adds 8 kN to one part. Check runs the load test.',
      debrief:
        'Upgrading the strong parts would have bought nothing. Failure investigations start the same way: find the part ' +
        'that gave first, then ask why it was the weakest.',
      leavesOut:
        'Real parts also fail by fatigue, rust and a bad joint, not only by pulling apart, and their strength has a spread ' +
        'rather than one number.',
    },
    tradeoff: {
      idea: 'Trade-offs',
      title: 'Fast, loaded, or safe?',
      story:
        "In Gravity, an astronaut crosses space on a jetpack. NASA's real one could change its speed by about 24 m/s in " +
        'all, and stopping costs as much fuel as starting. The crew at an airlock 120 m away needs a 60 kg repair kit ' +
        'within 20 seconds.',
      task: 'Choose a cruise speed and what to carry. Arrive within 20 s, bring the kit, and keep 4 m/s of fuel back for a mistake.',
      debrief:
        'Every metre per second is paid twice, once to go and once to stop, and the kit makes each one dearer. Much of ' +
        'engineering is this: three goals pulling against each other, and a narrow window where all of them hold.',
      leavesOut: 'Burns take time, the pack has a top thrust, and real orbits curve. This step treats space as flat and every burn as instant.',
    },
    'check-ai': {
      idea: 'Checking the AI',
      title: 'Iris says it will hold',
      story: 'The owner now wants a shelf for a 1.5 m alcove, with the same books on it, and Iris has an answer ready.',
      task:
        "Test Iris's plan. If it fails, fix it: change the plank, add a third bracket in the middle, or both. The limit is " +
        'still the span divided by 200.',
      debrief:
        'Iris carried a rule from the short shelf to the long one, but sag grows with the fourth power of the span: half a ' +
        'metre more is five times the sag. A confident answer is where checking starts. The model, the numbers and you ' +
        'are the check.',
      leavesOut: 'Everything the first shelf left out, and the middle bracket, which is treated as perfectly stiff.',
    },
  },

  estimate: {
    guess: 'Your guess',
    close: 'Close enough. A resting body gives off about 100 W.',
    low: (times: string) => `About ${times} times too low.`,
    high: (times: string) => `About ${times} times too high.`,
  },
  shelf: {
    plank: 'Plank',
    brackets: 'Brackets',
    alcove: (q: string) => `Alcove ${q}`,
    sag: (q: string) => `Sags ${q}`,
    limit: (q: string) => `allowed ${q}`,
    ok: 'It holds, with no sag you would notice.',
    fail: 'It sags more than a shelf should.',
  },
  iris: {
    plan: 'For the 1.5 m alcove, the same 18 mm plank will do on two brackets. It held on the short shelf.',
  },
  power: {
    volts: 'Voltage',
    cables: 'Cables',
    amps: (q: string) => `${q} in all`,
    perCable: (q: string) => `${q} on each cable`,
    arcs: 'Too high for these towers: the air between the wires breaks down and arcs.',
    hot: (times: string) => `Each cable carries ${times} times its rating. It overheats and sags into the trees.`,
    vapour: 'Each cable carries over a thousand times its rating. It turns to vapour.',
    ok: 'Three gigawatts, delivered.',
  },
  margin: {
    rope: 'Rope',
    load: (q: string) => `Load ${q}`,
    breaks: (q: string) => `Breaks at ${q}`,
    factor: (times: string) => `${times} times the load`,
    ok: 'Five times stronger than the load, as the brief asks.',
    fail: (times: string) => `Only ${times} times the load. The brief asks for five.`,
  },
  chain: {
    parts: { anchor: 'Wall anchor', rope: 'Rope', shackle: 'Shackle', hook: 'Hook' },
    upgrade: 'Upgrade',
    undo: 'Undo',
    left: (n: number, of: number) => `${n} of ${of} upgrades left`,
    target: (q: string) => `Must hold ${q}`,
    broke: {
      anchor: (q: string) => `The wall anchor pulled out at ${q}.`,
      rope: (q: string) => `The rope snapped at ${q}.`,
      shackle: (q: string) => `The shackle broke at ${q}.`,
      hook: (q: string) => `The hook bent open at ${q}.`,
    },
    ok: (q: string) => `It held ${q}.`,
  },
  jetpack: {
    speed: 'Cruise speed',
    kit: 'Carry the repair kit (60 kg)',
    budget: (q: string) => `Fuel for ${q} of speed change`,
    used: (q: string) => `Going and stopping use ${q}`,
    arrive: (q: string) => `Arrive in ${q}`,
    kitMissing: 'The crew needs the kit. Carry it.',
    fuel: 'Not enough fuel to stop. You sail past the airlock.',
    reserve: (q: string) => `Only ${q} left for a mistake. Keep 4 m/s.`,
    late: (q: string) => `${q} is too late: the airlock closes at 20 s.`,
    ok: 'On time, with the kit, and fuel to spare.',
  },

  finish: {
    title: 'You finished Foundations',
    lede: 'Seven ideas, each one checked. Now choose where to go deeper.',
  },
  disciplines: {
    label: 'Choose your discipline',
    title: 'Where next?',
    lede: 'Software is the full course, open today. Power and Energy and Civil have five-task previews you can play now; vote for the full course you want, and the most wanted is built first.',
    preview: 'Preview',
    tryPreview: 'Try the preview',
    gridCourse: 'Run the town grid (2 levels, early)',
    here: 'You are in this preview.',
    backToFoundations: 'Back to Foundations',
    vote: 'I want the full course',
    voted: 'Counted. We build the most wanted first.',
    software: {
      name: 'Software',
      what: 'Real Linux, real git and real code in your browser, checked on what you leave behind. From your first command to reviewing what a coding agent writes.',
      cta: 'Start level 0',
    },
    power: {
      name: 'Power and Energy',
      what: 'A solar roof in five tasks: point the panels, size the array, a battery for the night, the cable to the house.',
    },
    civil: {
      name: 'Civil',
      what: 'A house in five tasks: rafters under snow, a beam over a door, footings, and bracing against the wind.',
    },
  },
  landing: {
    label: 'New: Foundations',
    title: 'Seven ideas every engineer uses, in fifteen minutes',
    lede: 'Film scenes, shelves, ropes and power lines. Make each one work and the page checks it. No download, works on a phone.',
    cta: 'Try Foundations',
  },
  bar: 'Foundations',
};

/* ── Arabic ───────────────────────────────────────────────────────────────── */

export const ar: typeof en = {
  docTitle: 'الأساسيات · EngineerOS',
  kicker: 'سبع أفكار يستعملها كل مهندس',
  title: 'الأساسيات',
  lede:
    'سبع مهام صغيرة، نحو ربع ساعة كلها. تبدأ كل واحدة من مشهد في فيلم أو من شيء تراه كل يوم، وتطلب منك أن تجعل شيئًا يعمل، ' +
    'والصفحة تتحقّق منه. بلا حساب وبلا تنزيل.',
  honest:
    'النماذج هنا مبسّطة عن قصد، وكل خطوة تقول ما يتركه نموذجها جانبًا. إتمام الأساسيات يعني أنك استعملت الأفكار الجوهرية، ' +
    'لا أنك تستطيع تصميم شيء حقيقي.',
  progress: (done: number, total: number) => `أنجزت ${done} من ${total}`,
  stepLabel: (n: number) => `الخطوة ${n}`,
  watch: 'شاهد المقطع القصير',
  watchNote: 'يُفتح على YouTube',
  check: 'تحقّق',
  testing: 'جارٍ الاختبار…',
  done: 'تمّت',
  next: 'الخطوة التالية',
  leavesOutLabel: 'ما يتركه هذا النموذج جانبًا',
  locked: 'أتمّ الخطوة السابقة لتُفتح هذه.',
  irisName: 'Iris',

  steps: {
    estimate: {
      idea: 'التقدير',
      title: 'هل الخطأ عشرة أضعاف أم ألف؟',
      story:
        'في فيلم The Matrix تستغلّ الآلات حرارة أجساد البشر مصدرًا للطاقة. قبل أن يفحص المهندس ادّعاءً كهذا يسأل سؤالًا واحدًا أولًا: ' +
        'ما حجم هذا الرقم تقريبًا؟',
      task:
        'قدّر كم واطًا يطلق جسم إنسان مرتاح من الحرارة. تلميح: تأكل نحو 2,000 سعرة حرارية (كيلوكالوري) في اليوم، ويخرج معظمها ' +
        'حرارةً. الكيلوكالوري نحو 4,200 جول، واليوم 86,400 ثانية، والواط جول واحد في الثانية.',
      debrief:
        'نحو 100 واط، مثل مصباح قديم ساطع. محطة كهرباء بمليار واط تحتاج إلى عشرة ملايين إنسان، قبل أن يضيع معظم حرارتهم في أي محرّك. ' +
        'أن تعرف حجم الرقم في دقيقة يخبرك هل يستحق الادّعاء ساعة من وقتك.',
      leavesOut: 'حرارة الجسم تتغيّر مع حجمه ونشاطه وحرارة الغرفة. كل ما تطلبه هذه الخطوة أن تكون قريبًا بنحو ثلاثة أضعاف.',
    },
    loads: {
      idea: 'القوى والأحمال',
      title: 'هل سينحني الرفّ؟',
      story:
        'تجويف عرضه متر واحد، وحاملان، و25 كغ من الكتب. الرفّ الذي ترى انحناءه يبدو مكسورًا حتى لو لم يكن، لذلك يُبقي البنّاؤون ' +
        'الانحناء أقل من جزء من مئتين من البحر. وهو هنا 5 مم.',
      task: 'اختر أرقّ لوح صنوبر لا ينحني أكثر من 5 مم. الخشب الأسمك أغلى.',
      debrief:
        'الصلابة تزداد بمكعّب السماكة: لوح 18 مم أسمك من لوح 12 مم مرة ونصفًا، وأصلب منه بنحو 3.4 مرات. لهذا تقف روافد الأرضيات ' +
        'طويلة ورفيعة بدل أن تستلقي عريضة ومسطّحة.',
      leavesOut: 'العُقد في الخشب، والحوامل التي تنثني، والبراغي التي تنخلع، والخشب الذي يزحف تحت حمل يبقى عليه سنوات.',
    },
    power: {
      idea: 'الطاقة والقدرة',
      title: 'ثلاثة غيغاواط في سلك',
      story:
        'في فيلم Iron Man يُخرج مفاعل توني ستارك ثلاثة غيغاواط، أكثر من سدّ هوفر، عبر بضعة أسلاك رفيعة. القدرة هي الفولت مضروبًا ' +
        'في الأمبير، فالقدرة نفسها قد تكون تيارًا صغيرًا بجهد عالٍ أو تيارًا هائلًا بجهد منخفض.',
      task:
        'أوصل 3 غيغاواط عبر هذا الخط. كل كبل يحمل 3,100 أمبير على الأكثر، والأبراج تحتمل حتى 1,000,000 فولت. اختر الجهد وعدد الكبلات.',
      debrief:
        'ارفع الجهد ينخفض التيار، والتيار هو ما يسخّن السلك. هذه الحقيقة وحدها هي سبب نقل الشبكة للكهرباء بمئات آلاف الفولتات ' +
        'وخفضها قرب بيتك. خط Pacific DC Intertie الحقيقي ينقل 3.1 غيغاواط بجهد ±500,000 فولت، على كبلين.',
      leavesOut: 'الفاقد على طول الخط، والتيار المتردّد مقابل المستمر، والمحوّلات في الطرفين، والطقس.',
    },
    margin: {
      idea: 'هوامش الأمان',
      title: 'ما القوّة الكافية؟',
      story:
        'رافعة ترفع 500 كغ. الحبل الذي يحمل 500 كغ بالضبط ينقطع أول مرة يتأرجح فيها الحمل، أو يبلى الحبل، أو يحمّله أحد 520 كغ. ' +
        'لذلك يطلب التكليف حبلًا أقوى من الحمل خمس مرات.',
      task: 'اختر أرقّ حبل فولاذي لا ينقطع تحت أقل من خمسة أضعاف الحمل.',
      debrief:
        'الهامش يدفع ثمن كل ما لم يقِسه أحد: البلى، والهزّة حين يبدأ الحمل بالحركة، والوصلة الرديئة. يختار المهندسون المُعامل بحسب كلفة ' +
        'الفشل، ولهذا يتطلب رفع الناس أكثر مما يتطلبه رفع الطوب.',
      leavesOut:
        'الحبال الحقيقية يحدّد صانعها قوّتها، وأنظمة الرفع تحدّد المُعامل. القاعدة التقريبية هنا، قوة القطع نحو 0.6 × d² كيلونيوتن، ' +
        'لهذه الخطوة فقط.',
    },
    weakest: {
      idea: 'الفشل',
      title: 'أيّ جزء ينكسر أولًا؟',
      story:
        'حمل معلّق بخطّاف، على شكّال، على حبل، من مرساة في الجدار. السلسلة كلها لا تحمل إلا ما يحمله أضعف أجزائها، مهما قوي الباقي.',
      task: 'اجعل السلسلة تحمل 30 كيلونيوتن. لديك ميزانية لثلاث ترقيات، وكل ترقية تضيف 8 كيلونيوتن إلى جزء واحد. «تحقّق» يُجري اختبار الحمل.',
      debrief:
        'ترقية الأجزاء القوية لم تكن لتشتري شيئًا. تحقيقات الفشل تبدأ بالطريقة نفسها: جد الجزء الذي استسلم أولًا، ثم اسأل لماذا كان الأضعف.',
      leavesOut: 'الأجزاء الحقيقية تفشل أيضًا بالكلال والصدأ والوصلات الرديئة، لا بالشدّ وحده، وقوّتها نطاق لا رقم واحد.',
    },
    tradeoff: {
      idea: 'الموازنات',
      title: 'سريع، أم محمّل، أم آمن؟',
      story:
        'في فيلم Gravity يعبر رائد فضاء الفراغ بحقيبة دفع نفّاثة. الحقيبة الحقيقية لدى NASA كانت تغيّر سرعتها بنحو 24 م/ث فقط في المجمل، ' +
        'والتوقّف يكلّف من الوقود ما يكلّفه الانطلاق. والطاقم عند بوابة على بعد 120 م يحتاج إلى عدّة إصلاح وزنها 60 كغ خلال 20 ثانية.',
      task: 'اختر سرعة الانتقال وما تحمله. اصل خلال 20 ثانية، وأحضر العدّة، واحتفظ بوقود يكفي 4 م/ث لخطأ ما.',
      debrief:
        'كل متر في الثانية يُدفع ثمنه مرتين، مرة للانطلاق ومرة للتوقّف، والعدّة تجعل كل واحدة أغلى. كثير من الهندسة هو هذا: ثلاثة أهداف ' +
        'يشدّ بعضها ضد بعض، ونافذة ضيّقة تتحقق فيها كلها.',
      leavesOut: 'الاحتراق يستغرق وقتًا، وللحقيبة دفع أقصى، والمدارات الحقيقية منحنية. هذه الخطوة تعامل الفضاء كأنه مسطّح وكل احتراق كأنه لحظي.',
    },
    'check-ai': {
      idea: 'التحقّق من الذكاء الاصطناعي',
      title: 'Iris تقول إنه سيصمد',
      story: 'يريد صاحب البيت الآن رفًّا لتجويف عرضه 1.5 م، عليه الكتب نفسها، و Iris لديها جواب جاهز.',
      task: 'اختبر خطة Iris. إن فشلت فأصلحها: غيّر اللوح، أو أضف حاملًا ثالثًا في الوسط، أو كليهما. الحدّ ما زال البحر مقسومًا على 200.',
      debrief:
        'نقلت Iris قاعدة من الرفّ القصير إلى الطويل، لكن الانحناء يزداد بالقوة الرابعة للبحر: نصف متر إضافي يعني خمسة أضعاف الانحناء. ' +
        'الجواب الواثق هو حيث يبدأ التحقّق. النموذج والأرقام وأنت، هؤلاء هم التحقّق.',
      leavesOut: 'كل ما تركه الرفّ الأول جانبًا، والحامل الأوسط الذي يُعامَل كأنه صلب تمامًا.',
    },
  },

  estimate: {
    guess: 'تقديرك',
    close: 'قريب بما يكفي. جسم الإنسان المرتاح يطلق نحو 100 واط.',
    low: (times: string) => `أقل بنحو ${times} مرات.`,
    high: (times: string) => `أكثر بنحو ${times} مرات.`,
  },
  shelf: {
    plank: 'اللوح',
    brackets: 'الحوامل',
    alcove: (q: string) => `التجويف ${q}`,
    sag: (q: string) => `ينحني ${q}`,
    limit: (q: string) => `المسموح ${q}`,
    ok: 'يصمد، بلا انحناء تلاحظه.',
    fail: 'ينحني أكثر مما ينبغي لرفّ.',
  },
  iris: {
    plan: 'لتجويف 1.5 م يكفي لوح 18 مم نفسه على حاملين. لقد صمد على الرفّ القصير.',
  },
  power: {
    volts: 'الجهد',
    cables: 'الكبلات',
    amps: (q: string) => `${q} في المجموع`,
    perCable: (q: string) => `${q} على كل كبل`,
    arcs: 'أعلى مما تحتمله هذه الأبراج: ينهار الهواء بين الأسلاك ويقفز القوس الكهربائي.',
    hot: (times: string) => `كل كبل يحمل ${times} أضعاف قدرته. يسخن ويتدلّى حتى الأشجار.`,
    vapour: 'كل كبل يحمل أكثر من ألف ضعف قدرته. يتحوّل إلى بخار.',
    ok: 'ثلاثة غيغاواط، وصلت.',
  },
  margin: {
    rope: 'الحبل',
    load: (q: string) => `الحمل ${q}`,
    breaks: (q: string) => `ينقطع عند ${q}`,
    factor: (times: string) => `${times} أضعاف الحمل`,
    ok: 'أقوى من الحمل خمس مرات، كما يطلب التكليف.',
    fail: (times: string) => `فقط ${times} أضعاف الحمل. التكليف يطلب خمسة.`,
  },
  chain: {
    parts: { anchor: 'مرساة الجدار', rope: 'الحبل', shackle: 'الشكّال', hook: 'الخطّاف' },
    upgrade: 'رقِّ',
    undo: 'تراجع',
    left: (n: number, of: number) => `بقي ${n} من ${of} ترقيات`,
    target: (q: string) => `يجب أن تحمل ${q}`,
    broke: {
      anchor: (q: string) => `انخلعت مرساة الجدار عند ${q}.`,
      rope: (q: string) => `انقطع الحبل عند ${q}.`,
      shackle: (q: string) => `انكسر الشكّال عند ${q}.`,
      hook: (q: string) => `انفتح الخطّاف عند ${q}.`,
    },
    ok: (q: string) => `صمدت تحت ${q}.`,
  },
  jetpack: {
    speed: 'سرعة الانتقال',
    kit: 'احمل عدّة الإصلاح (60 كغ)',
    budget: (q: string) => `وقود يكفي لتغيير السرعة ${q}`,
    used: (q: string) => `الانطلاق والتوقّف يستهلكان ${q}`,
    arrive: (q: string) => `تصل خلال ${q}`,
    kitMissing: 'الطاقم يحتاج إلى العدّة. احملها.',
    fuel: 'لا يكفي الوقود للتوقّف. تتجاوز البوابة.',
    reserve: (q: string) => `بقي ${q} فقط لأي خطأ. احتفظ بـ 4 م/ث.`,
    late: (q: string) => `${q} متأخر جدًا: البوابة تُغلق عند 20 ثانية.`,
    ok: 'في الوقت، ومعك العدّة، ووقود زائد.',
  },

  finish: {
    title: 'أتممت الأساسيات',
    lede: 'سبع أفكار، تُحقّق من كل واحدة منها. اختر الآن أين تتعمّق.',
  },
  disciplines: {
    label: 'اختر تخصّصك',
    title: 'إلى أين بعد ذلك؟',
    lede: 'البرمجيات مسار كامل متاح اليوم. وللطاقة والهندسة المدنية معاينتان من خمس مهام يمكنك تجربتهما الآن؛ صوّت للمسار الكامل الذي تريده، والأكثر طلبًا يُبنى أولًا.',
    preview: 'معاينة',
    tryPreview: 'جرّب المعاينة',
    gridCourse: 'شغّل شبكة البلدة (مستويان، نسخة مبكرة)',
    here: 'أنت في هذه المعاينة.',
    backToFoundations: 'عودة إلى الأساسيات',
    vote: 'أريد المسار الكامل',
    voted: 'سُجّل صوتك. نبني الأكثر طلبًا أولًا.',
    software: {
      name: 'البرمجيات',
      what: 'Linux حقيقي و git حقيقي وكود حقيقي داخل متصفّحك، يُتحقّق منه بما تتركه خلفك. من أول أمر لك إلى مراجعة ما يكتبه وكيل برمجي.',
      cta: 'ابدأ المستوى 0',
    },
    power: {
      name: 'الطاقة والقوى الكهربائية',
      what: 'سطح شمسي في خمس مهام: وجّه الألواح، وحدّد عددها، وبطارية لليل، والكبل إلى البيت.',
    },
    civil: {
      name: 'الهندسة المدنية',
      what: 'بيت في خمس مهام: روافد السقف تحت الثلج، وجسر فوق باب، والأساسات، والتقوية ضد الرياح.',
    },
  },
  landing: {
    label: 'جديد: الأساسيات',
    title: 'سبع أفكار يستعملها كل مهندس، في ربع ساعة',
    lede: 'مشاهد أفلام، ورفوف، وحبال، وخطوط كهرباء. اجعل كل واحدة تعمل والصفحة تتحقّق منها. بلا تنزيل، وتعمل على الهاتف.',
    cta: 'جرّب الأساسيات',
  },
  bar: 'الأساسيات',
};

/* ── German ───────────────────────────────────────────────────────────────── */

export const de: typeof en = {
  docTitle: 'Grundlagen · EngineerOS',
  kicker: 'Sieben Ideen, die jede Ingenieurin und jeder Ingenieur nutzt',
  title: 'Grundlagen',
  lede:
    'Sieben kleine Aufgaben, zusammen etwa eine Viertelstunde. Jede beginnt mit einer Filmszene oder einem Alltagsgegenstand ' +
    'und verlangt, dass du etwas zum Funktionieren bringst, und die Seite prüft es. Kein Konto, nichts herunterzuladen.',
  honest:
    'Die Modelle hier sind absichtlich vereinfacht, und jeder Schritt sagt, was sein Modell weglässt. Wer die Grundlagen ' +
    'abschließt, hat die Kernideen angewendet, kann damit aber noch nichts Reales auslegen.',
  progress: (done: number, total: number) => `${done} von ${total} erledigt`,
  stepLabel: (n: number) => `Schritt ${n}`,
  watch: 'Den Short ansehen',
  watchNote: 'öffnet YouTube',
  check: 'Prüfen',
  testing: 'Wird getestet…',
  done: 'Erledigt',
  next: 'Nächster Schritt',
  leavesOutLabel: 'Was dieses Modell weglässt',
  locked: 'Schließe den Schritt darüber ab, um diesen zu öffnen.',
  irisName: 'Iris',

  steps: {
    estimate: {
      idea: 'Abschätzen',
      title: 'Um das Zehnfache daneben oder um das Tausendfache?',
      story:
        'In Matrix ernten Maschinen die Körperwärme von Menschen als Energie. Bevor eine Ingenieurin so eine Behauptung prüft, ' +
        'fragt sie zuerst nur eins: Wie groß ist die Zahl ungefähr?',
      task:
        'Schätze, wie viel Watt ein ruhender Mensch als Wärme abgibt. Ein Hinweis: Du isst etwa 2.000 Kilokalorien am Tag, und ' +
        'fast alles verlässt den Körper als Wärme. Eine Kilokalorie sind etwa 4.200 Joule, ein Tag hat 86.400 Sekunden, und ein ' +
        'Watt ist ein Joule pro Sekunde.',
      debrief:
        'Etwa 100 W, eine helle alte Glühbirne. Ein Kraftwerk mit einer Milliarde Watt bräuchte zehn Millionen Menschen, bevor ' +
        'ein Motor den Großteil ihrer Wärme verliert. Wer die Größenordnung in einer Minute trifft, weiß, ob eine Behauptung ' +
        'eine Stunde verdient.',
      leavesOut: 'Körperwärme hängt von Größe, Aktivität und Raum ab. Dieser Schritt verlangt nur, dass du auf etwa das Dreifache genau liegst.',
    },
    loads: {
      idea: 'Kräfte und Lasten',
      title: 'Hängt das Regal durch?',
      story:
        'Eine Nische von einem Meter, zwei Halter und 25 kg Bücher. Ein Regal, das sichtbar durchhängt, wirkt kaputt, auch wenn ' +
        'es hält. Deshalb halten Handwerker die Durchbiegung unter einem Zweihundertstel der Spannweite, hier 5 mm.',
      task: 'Wähle das dünnste Kiefernbrett, das sich höchstens 5 mm durchbiegt. Dickeres Holz kostet mehr.',
      debrief:
        'Die Steifigkeit wächst mit der dritten Potenz der Dicke: 18 mm ist anderthalbmal so dick wie 12 mm und etwa 3,4-mal so ' +
        'steif. Deshalb stehen Deckenbalken hoch und schmal, statt flach und breit zu liegen.',
      leavesOut: 'Äste im Holz, Halter, die nachgeben, Schrauben, die ausreißen, und Holz, das unter einer Dauerlast über Jahre kriecht.',
    },
    power: {
      idea: 'Energie und Leistung',
      title: 'Drei Gigawatt durch einen Draht',
      story:
        'In Iron Man liefert Tony Starks Reaktor drei Gigawatt, mehr als der Hoover-Damm, über ein paar dünne Drähte. Leistung ist ' +
        'Spannung mal Strom, also kann dieselbe Leistung ein kleiner Strom bei hoher Spannung sein oder ein riesiger bei niedriger.',
      task:
        'Bring 3 GW durch diese Leitung. Jedes Kabel trägt höchstens 3.100 A, und die Masten halten bis 1.000.000 V. Wähle die ' +
        'Spannung und die Zahl der Kabel.',
      debrief:
        'Steigt die Spannung, sinkt der Strom, und der Strom ist es, der einen Draht erhitzt. Allein deshalb überträgt das Netz ' +
        'Strom mit Hunderttausenden Volt und spannt ihn erst nahe deinem Zuhause herunter. Der echte Pacific DC Intertie überträgt ' +
        '3,1 GW bei ±500.000 V auf zwei Kabeln.',
      leavesOut: 'Verluste entlang der Leitung, Wechsel- gegen Gleichstrom, die Transformatoren an beiden Enden und das Wetter.',
    },
    margin: {
      idea: 'Sicherheitsreserven',
      title: 'Wie stark ist stark genug?',
      story:
        'Ein Hebezug hebt 500 kg. Ein Seil, das genau 500 kg hält, reißt beim ersten Schwingen der Last, wenn es verschleißt oder ' +
        'jemand 520 kg anhängt. Deshalb verlangt der Auftrag ein Seil, das fünfmal stärker ist als die Last.',
      task: 'Wähle das dünnste Stahlseil, das erst bei mindestens dem Fünffachen der Last reißt.',
      debrief:
        'Die Reserve bezahlt alles, was niemand gemessen hat: Verschleiß, den Ruck, wenn die Last anfährt, einen schlechten Spleiß. ' +
        'Der Faktor richtet sich danach, was ein Versagen kosten würde, deshalb verlangt das Heben von Menschen mehr als das von Ziegeln.',
      leavesOut:
        'Echte Seile werden vom Hersteller bewertet, und Hebevorschriften legen den Faktor fest. Die Faustregel hier, Bruchkraft ' +
        'etwa 0,6 × d² kN, gilt nur für diesen Schritt.',
    },
    weakest: {
      idea: 'Versagen',
      title: 'Welches Teil bricht zuerst?',
      story:
        'Eine Last hängt an einem Haken, an einem Schäkel, an einem Seil, an einem Anker in der Wand. Die ganze Kette hält nur so ' +
        'viel wie ihr schwächstes Teil, egal wie stark der Rest ist.',
      task: 'Bring die Kette auf 30 kN. Du kannst dir drei Verstärkungen leisten, jede gibt einem Teil 8 kN mehr. Prüfen startet den Lasttest.',
      debrief:
        'Die starken Teile zu verstärken, hätte nichts gebracht. Schadensanalysen beginnen genauso: Finde das Teil, das zuerst ' +
        'nachgab, und frage dann, warum es das schwächste war.',
      leavesOut:
        'Echte Teile versagen auch durch Ermüdung, Rost und schlechte Verbindungen, nicht nur durch Zug, und ihre Festigkeit ist ' +
        'eine Streuung statt einer Zahl.',
    },
    tradeoff: {
      idea: 'Zielkonflikte',
      title: 'Schnell, beladen oder sicher?',
      story:
        'In Gravity überquert ein Astronaut den Raum mit einem Raketenrucksack. Der echte der NASA konnte seine Geschwindigkeit ' +
        'insgesamt nur um etwa 24 m/s ändern, und Bremsen kostet so viel Treibstoff wie Anfahren. Die Crew an einer Luftschleuse ' +
        '120 m entfernt braucht innerhalb von 20 Sekunden einen 60 kg schweren Reparaturkoffer.',
      task: 'Wähle eine Reisegeschwindigkeit und was du mitnimmst. Komm in 20 s an, bring den Koffer und halte 4 m/s Treibstoff für einen Fehler zurück.',
      debrief:
        'Jeder Meter pro Sekunde wird zweimal bezahlt, einmal fürs Losfliegen und einmal fürs Bremsen, und der Koffer macht jeden ' +
        'teurer. Viel Ingenieursarbeit ist genau das: drei Ziele, die gegeneinander ziehen, und ein schmales Fenster, in dem alle halten.',
      leavesOut: 'Schübe brauchen Zeit, der Rucksack hat einen Maximalschub, und echte Bahnen sind gekrümmt. Hier ist der Raum flach und jeder Schub sofort.',
    },
    'check-ai': {
      idea: 'Die KI überprüfen',
      title: 'Iris sagt, es hält',
      story: 'Jetzt soll ein Regal in eine 1,5 m breite Nische, mit denselben Büchern, und Iris hat schon eine Antwort.',
      task:
        'Teste den Plan von Iris. Wenn er scheitert, verbessere ihn: ein anderes Brett, ein dritter Halter in der Mitte, oder beides. ' +
        'Die Grenze ist weiter die Spannweite geteilt durch 200.',
      debrief:
        'Iris hat eine Regel vom kurzen Regal aufs lange übertragen, aber die Durchbiegung wächst mit der vierten Potenz der ' +
        'Spannweite: ein halber Meter mehr ist die fünffache Durchbiegung. Eine selbstsichere Antwort ist der Anfang der Prüfung. ' +
        'Das Modell, die Zahlen und du sind die Prüfung.',
      leavesOut: 'Alles, was das erste Regal wegließ, und den mittleren Halter, der als vollkommen steif gilt.',
    },
  },

  estimate: {
    guess: 'Deine Schätzung',
    close: 'Nah genug. Ein ruhender Körper gibt etwa 100 W ab.',
    low: (times: string) => `Etwa ${times}-mal zu niedrig.`,
    high: (times: string) => `Etwa ${times}-mal zu hoch.`,
  },
  shelf: {
    plank: 'Brett',
    brackets: 'Halter',
    alcove: (q: string) => `Nische ${q}`,
    sag: (q: string) => `Biegt sich ${q} durch`,
    limit: (q: string) => `erlaubt ${q}`,
    ok: 'Es hält, ohne dass man eine Durchbiegung sieht.',
    fail: 'Es biegt sich mehr durch, als ein Regal sollte.',
  },
  iris: {
    plan: 'Für die 1,5-m-Nische reicht dasselbe 18-mm-Brett auf zwei Haltern. Beim kurzen Regal hat es gehalten.',
  },
  power: {
    volts: 'Spannung',
    cables: 'Kabel',
    amps: (q: string) => `${q} insgesamt`,
    perCable: (q: string) => `${q} pro Kabel`,
    arcs: 'Zu hoch für diese Masten: Die Luft zwischen den Leitern schlägt durch, ein Lichtbogen entsteht.',
    hot: (times: string) => `Jedes Kabel trägt das ${times}-Fache seiner Grenze. Es überhitzt und hängt bis in die Bäume durch.`,
    vapour: 'Jedes Kabel trägt mehr als das Tausendfache seiner Grenze. Es verdampft.',
    ok: 'Drei Gigawatt, angekommen.',
  },
  margin: {
    rope: 'Seil',
    load: (q: string) => `Last ${q}`,
    breaks: (q: string) => `Reißt bei ${q}`,
    factor: (times: string) => `das ${times}-Fache der Last`,
    ok: 'Fünfmal stärker als die Last, wie der Auftrag verlangt.',
    fail: (times: string) => `Nur das ${times}-Fache der Last. Der Auftrag verlangt das Fünffache.`,
  },
  chain: {
    parts: { anchor: 'Wandanker', rope: 'Seil', shackle: 'Schäkel', hook: 'Haken' },
    upgrade: 'Verstärken',
    undo: 'Zurück',
    left: (n: number, of: number) => `${n} von ${of} Verstärkungen übrig`,
    target: (q: string) => `Muss ${q} halten`,
    broke: {
      anchor: (q: string) => `Der Wandanker ist bei ${q} ausgerissen.`,
      rope: (q: string) => `Das Seil ist bei ${q} gerissen.`,
      shackle: (q: string) => `Der Schäkel ist bei ${q} gebrochen.`,
      hook: (q: string) => `Der Haken hat sich bei ${q} aufgebogen.`,
    },
    ok: (q: string) => `Sie hat ${q} gehalten.`,
  },
  jetpack: {
    speed: 'Reisegeschwindigkeit',
    kit: 'Den Reparaturkoffer mitnehmen (60 kg)',
    budget: (q: string) => `Treibstoff für ${q} Geschwindigkeitsänderung`,
    used: (q: string) => `Losfliegen und Bremsen verbrauchen ${q}`,
    arrive: (q: string) => `Ankunft nach ${q}`,
    kitMissing: 'Die Crew braucht den Koffer. Nimm ihn mit.',
    fuel: 'Nicht genug Treibstoff zum Bremsen. Du fliegst an der Schleuse vorbei.',
    reserve: (q: string) => `Nur ${q} übrig für einen Fehler. Halte 4 m/s zurück.`,
    late: (q: string) => `${q} ist zu spät: Die Schleuse schließt nach 20 s.`,
    ok: 'Pünktlich, mit dem Koffer und mit Treibstoff in Reserve.',
  },

  finish: {
    title: 'Du hast die Grundlagen abgeschlossen',
    lede: 'Sieben Ideen, jede geprüft. Wähle jetzt, wo du tiefer gehen willst.',
  },
  disciplines: {
    label: 'Wähle deine Fachrichtung',
    title: 'Wie geht es weiter?',
    lede: 'Software ist der volle Kurs, heute offen. Energietechnik und Bauingenieurwesen haben Vorschauen mit fünf Aufgaben, die du jetzt spielen kannst; stimm für den vollen Kurs, den du willst, und der gefragteste wird zuerst gebaut.',
    preview: 'Vorschau',
    tryPreview: 'Vorschau ausprobieren',
    gridCourse: 'Das Stadtnetz betreiben (2 Level, früh)',
    here: 'Du bist in dieser Vorschau.',
    backToFoundations: 'Zurück zu den Grundlagen',
    vote: 'Ich will den vollen Kurs',
    voted: 'Gezählt. Wir bauen zuerst, was am meisten gewünscht wird.',
    software: {
      name: 'Software',
      what: 'Echtes Linux, echtes git und echter Code im Browser, geprüft an dem, was du hinterlässt. Vom ersten Befehl bis zur Review dessen, was ein Coding-Agent schreibt.',
      cta: 'Level 0 starten',
    },
    power: {
      name: 'Energietechnik',
      what: 'Ein Solardach in fünf Aufgaben: Module ausrichten, die Anlage bemessen, ein Speicher für die Nacht, das Kabel ins Haus.',
    },
    civil: {
      name: 'Bauingenieurwesen',
      what: 'Ein Haus in fünf Aufgaben: Sparren unter Schnee, ein Sturz über einem Tor, Fundamente und Aussteifung gegen Wind.',
    },
  },
  landing: {
    label: 'Neu: Grundlagen',
    title: 'Sieben Ideen, die alle in der Technik nutzen, in einer Viertelstunde',
    lede: 'Filmszenen, Regale, Seile und Stromleitungen. Bring jede zum Funktionieren, und die Seite prüft es. Kein Download, läuft auf dem Handy.',
    cta: 'Grundlagen ausprobieren',
  },
  bar: 'Grundlagen',
};

/* ── Japanese ─────────────────────────────────────────────────────────────── */

export const ja: typeof en = {
  docTitle: '基礎 · EngineerOS',
  kicker: 'すべてのエンジニアが使う7つの考え方',
  title: '基礎',
  lede:
    '小さな課題が7つ、全部で15分ほど。どれも映画の一場面か身近なものから始まり、何かをうまく動かすよう求めます。' +
    '結果はページが確かめます。アカウントもダウンロードも不要です。',
  honest:
    'ここのモデルはわざと単純にしてあり、各ステップでそのモデルが省いていることを示します。基礎を終えたことは、' +
    '中心となる考え方を使ったという意味であり、実物を設計できるという意味ではありません。',
  progress: (done: number, total: number) => `${total}つ中${done}つ完了`,
  stepLabel: (n: number) => `ステップ ${n}`,
  watch: 'ショート動画を見る',
  watchNote: 'YouTubeで開きます',
  check: '確かめる',
  testing: 'テスト中…',
  done: '完了',
  next: '次のステップ',
  leavesOutLabel: 'このモデルが省いていること',
  locked: '上のステップを終えると開きます。',
  irisName: 'Iris',

  steps: {
    estimate: {
      idea: '見積もり',
      title: '10倍の違いか、1000倍の違いか',
      story:
        '映画『マトリックス』では、機械が人間の体温を電力として集めています。こうした主張を確かめる前に、' +
        'エンジニアはまずひとつだけ問います。その数はだいたいどのくらいの大きさか。',
      task:
        '安静にしている人が熱として何ワット出しているか見積もってください。ヒント: 人は1日に約2,000キロカロリーを食べ、' +
        'そのほとんどが熱として出ていきます。1キロカロリーは約4,200ジュール、1日は86,400秒、1ワットは1秒あたり1ジュールです。',
      debrief:
        '約100 W、明るい昔の電球1個分です。10億ワットの発電所には1,000万人が必要で、しかもどんなエンジンもその熱の大半を失います。' +
        '1分で桁をつかめれば、その主張に1時間かける価値があるかどうかがわかります。',
      leavesOut: '体温の放出は体格や活動、部屋によって変わります。このステップが求めるのは、3倍程度の範囲に入ることだけです。',
    },
    loads: {
      idea: '力と荷重',
      title: '棚はたわむか',
      story:
        '幅1メートルのくぼみ、ブラケット2つ、本が25 kg。目に見えてたわむ棚は、壊れていなくても壊れて見えます。' +
        'そこで職人は、たわみをスパンの200分の1以下に抑えます。ここでは5 mmです。',
      task: 'たわみが5 mm以下になる、いちばん薄いマツの板を選んでください。厚い板ほど高くなります。',
      debrief:
        '曲げにくさは厚さの3乗で増えます。18 mmは12 mmの1.5倍の厚さで、約3.4倍たわみにくくなります。' +
        'だから床の根太は、平たく広くではなく、高く細く立てて使われます。',
      leavesOut: '木の節、たわむブラケット、抜けるねじ、そして何年も荷重を受けてじわじわ変形する木材のクリープ。',
    },
    power: {
      idea: 'エネルギーと電力',
      title: '電線に3ギガワット',
      story:
        '映画『アイアンマン』では、トニー・スタークのリアクターが、フーバーダムを超える3ギガワットを数本の細い線で送ります。' +
        '電力は電圧かける電流なので、同じ電力でも高電圧の小さな電流にも、低電圧の巨大な電流にもなります。',
      task:
        'この送電線で3 GWを送ってください。ケーブル1本は最大3,100 Aまで、鉄塔は1,000,000 Vまで耐えます。電圧とケーブルの本数を選んでください。',
      debrief:
        '電圧を上げれば電流は下がり、電線を熱くするのは電流です。この事実ひとつが、送電網が数十万ボルトで電気を運び、' +
        '家の近くで電圧を下げる理由です。実在のPacific DC Intertieは、±500,000 Vで3.1 GWを2本のケーブルで送っています。',
      leavesOut: '線路に沿った損失、交流と直流の違い、両端の変圧器、そして天候。',
    },
    margin: {
      idea: '安全率',
      title: 'どれだけ強ければ十分か',
      story:
        'ホイストが500 kgを持ち上げます。ちょうど500 kgに耐えるロープは、荷が揺れたとき、ロープが摩耗したとき、' +
        '誰かが520 kgを吊ったとき、最初の一度で切れます。だから仕様は、荷重の5倍の強さのロープを求めています。',
      task: '荷重の5倍以上で初めて切れる、いちばん細いワイヤーロープを選んでください。',
      debrief:
        '安全率は、誰も測っていないものすべての代金です。摩耗、荷が動き出すときの衝撃、まずい編み込み。エンジニアは失敗の代償から' +
        '係数を選びます。だから人を吊るにはレンガを吊るより大きな係数が求められます。',
      leavesOut:
        '実際のロープの強さはメーカーが定め、係数は吊り上げの規則で決まります。ここで使う目安、破断力が約0.6 × d² kNというのは、' +
        'このステップのためだけのものです。',
    },
    weakest: {
      idea: '破壊',
      title: 'どの部品が先に壊れるか',
      story:
        '荷はフックに、フックはシャックルに、シャックルはロープに、ロープは壁のアンカーに吊られています。' +
        'ほかがどれだけ強くても、全体が耐えられるのはいちばん弱い部品の分だけです。',
      task: 'この連なりが30 kNに耐えるようにしてください。強化は3回まで、1回で1つの部品に8 kN加わります。「確かめる」で荷重試験をします。',
      debrief:
        '強い部品を強化しても何も得られませんでした。事故調査も同じように始まります。最初に壊れた部品を見つけ、' +
        'なぜそれがいちばん弱かったのかを問うのです。',
      leavesOut: '実際の部品は引っ張りだけでなく、疲労やさび、継ぎ目の不良でも壊れます。強さも1つの数ではなく、ばらつきを持ちます。',
    },
    tradeoff: {
      idea: 'トレードオフ',
      title: '速さか、荷物か、安全か',
      story:
        '映画『ゼロ・グラビティ』では、宇宙飛行士がジェットパックで宇宙を渡ります。NASAの実物は、速度を合計で約24 m/sしか' +
        '変えられず、止まるにも動き出すのと同じだけ燃料を使います。120 m先のエアロックの乗員は、20秒以内に60 kgの修理キットを必要としています。',
      task: '巡航速度と、何を運ぶかを選んでください。20秒以内に着き、キットを届け、ミスに備えて4 m/s分の燃料を残すこと。',
      debrief:
        '1 m/sごとに代金を2回払います。進むときに1回、止まるときに1回。キットはその1回1回を高くします。' +
        'エンジニアリングの多くはこれです。互いに引っ張り合う3つの目標と、すべてが成り立つ狭い窓。',
      leavesOut: '噴射には時間がかかり、パックには最大推力があり、実際の軌道は曲がっています。このステップでは宇宙を平らに、噴射を一瞬として扱います。',
    },
    'check-ai': {
      idea: 'AIを確かめる',
      title: 'Irisは大丈夫だと言う',
      story: '今度は同じ本を載せる、幅1.5 mのくぼみ用の棚が必要です。Irisはもう答えを用意しています。',
      task: 'Irisの案を試してください。だめなら直します。板を替える、真ん中に3つ目のブラケットを足す、あるいは両方。上限は今もスパン÷200です。',
      debrief:
        'Irisは短い棚のルールを長い棚にそのまま当てはめましたが、たわみはスパンの4乗で増えます。半メートル長いだけで、たわみは5倍です。' +
        '自信のある答えは、確かめることの出発点です。モデルと数字とあなたが、その確かめ役です。',
      leavesOut: '最初の棚が省いたものすべてと、完全に硬いものとして扱う真ん中のブラケット。',
    },
  },

  estimate: {
    guess: 'あなたの見積もり',
    close: '十分近いです。安静時の体は約100 Wを出しています。',
    low: (times: string) => `約${times}倍低すぎます。`,
    high: (times: string) => `約${times}倍高すぎます。`,
  },
  shelf: {
    plank: '板',
    brackets: 'ブラケット',
    alcove: (q: string) => `くぼみ ${q}`,
    sag: (q: string) => `たわみ ${q}`,
    limit: (q: string) => `許容 ${q}`,
    ok: '耐えます。たわみは目に見えません。',
    fail: '棚としてはたわみすぎです。',
  },
  iris: {
    plan: '1.5 mのくぼみでも、同じ18 mmの板をブラケット2つで使えば大丈夫です。短い棚では問題ありませんでした。',
  },
  power: {
    volts: '電圧',
    cables: 'ケーブル',
    amps: (q: string) => `合計 ${q}`,
    perCable: (q: string) => `ケーブル1本あたり ${q}`,
    arcs: 'この鉄塔には高すぎます。電線のあいだの空気が絶縁破壊し、アークが飛びます。',
    hot: (times: string) => `ケーブル1本が定格の${times}倍を運んでいます。過熱して木に触れるほど垂れ下がります。`,
    vapour: 'ケーブル1本が定格の1000倍以上を運んでいます。蒸発します。',
    ok: '3ギガワット、届きました。',
  },
  margin: {
    rope: 'ロープ',
    load: (q: string) => `荷重 ${q}`,
    breaks: (q: string) => `${q}で破断`,
    factor: (times: string) => `荷重の${times}倍`,
    ok: '仕様どおり、荷重の5倍の強さです。',
    fail: (times: string) => `荷重の${times}倍しかありません。仕様は5倍です。`,
  },
  chain: {
    parts: { anchor: '壁のアンカー', rope: 'ロープ', shackle: 'シャックル', hook: 'フック' },
    upgrade: '強化',
    undo: '戻す',
    left: (n: number, of: number) => `強化は残り${n}回(全${of}回)`,
    target: (q: string) => `${q}に耐えること`,
    broke: {
      anchor: (q: string) => `${q}で壁のアンカーが抜けました。`,
      rope: (q: string) => `${q}でロープが切れました。`,
      shackle: (q: string) => `${q}でシャックルが壊れました。`,
      hook: (q: string) => `${q}でフックが開いてしまいました。`,
    },
    ok: (q: string) => `${q}に耐えました。`,
  },
  jetpack: {
    speed: '巡航速度',
    kit: '修理キットを運ぶ(60 kg)',
    budget: (q: string) => `燃料は速度変化${q}分`,
    used: (q: string) => `出発と停止で${q}を使用`,
    arrive: (q: string) => `${q}で到着`,
    kitMissing: '乗員にはキットが必要です。運んでください。',
    fuel: '止まるだけの燃料がありません。エアロックを通り過ぎます。',
    reserve: (q: string) => `ミスに備えた燃料が${q}しか残りません。4 m/s残してください。`,
    late: (q: string) => `${q}では遅すぎます。エアロックは20秒で閉まります。`,
    ok: '時間内に、キットを持って、燃料にも余裕あり。',
  },

  finish: {
    title: '基礎を修了しました',
    lede: '7つの考え方を、すべて確かめました。次はどこを深めるか選んでください。',
  },
  disciplines: {
    label: '分野を選ぶ',
    title: '次はどこへ',
    lede: 'ソフトウェアは今日から学べる本コースです。電力・エネルギーと土木には、今すぐ遊べる5つの課題のプレビューがあります。学びたい本コースに投票してください。希望の多いものから作ります。',
    preview: 'プレビュー',
    tryPreview: 'プレビューを試す',
    gridCourse: '町の送電網を運用する(2レベル、先行版)',
    here: 'このプレビューの中にいます。',
    backToFoundations: '基礎に戻る',
    vote: '本コースを学びたい',
    voted: '数えました。希望の多いものから作ります。',
    software: {
      name: 'ソフトウェア',
      what: '本物のLinux、本物のgit、本物のコードをブラウザで。何を残したかで確かめます。最初のコマンドから、コーディングエージェントが書いたもののレビューまで。',
      cta: 'レベル0を始める',
    },
    power: {
      name: '電力・エネルギー',
      what: '5つの課題で太陽光の屋根を: パネルの向き、枚数、夜のための蓄電池、家までのケーブル。',
    },
    civil: {
      name: '土木',
      what: '5つの課題で家を: 雪の下の垂木、扉の上の梁、基礎、そして風に対する筋かい。',
    },
  },
  landing: {
    label: '新登場: 基礎',
    title: 'すべてのエンジニアが使う7つの考え方を、15分で',
    lede: '映画の場面、棚、ロープ、送電線。どれもうまく動かせば、ページが確かめます。ダウンロード不要、スマートフォンでも動きます。',
    cta: '基礎を試す',
  },
  bar: '基礎',
};
